type MatchOptions = {
  ignoreQuery?: boolean
}

const escapeForRegex = (str: string) =>
  str.replace(/[-\/\\^$+.()|[\]{}]/g, '\\$&').replace(/\*/g, '.*')

const createSingleMatcher = (
  pattern: string,
  { ignoreQuery = true }: MatchOptions = {}
): ((urlOrPath: string) => boolean) => {
  const qIndex = pattern.indexOf('?')
  const hasQueryInPattern = qIndex !== -1

  if (!hasQueryInPattern) {
    // Path-only pattern — allow optional ?query and #hash when ignoreQuery
    const escaped = escapeForRegex(pattern)
    const src = ignoreQuery
      ? '^' + escaped + '(?:\\?.*)?(?:#.*)?$'
      : '^' + escaped + '$'
    const rx = new RegExp(src)

    return (urlOrPath) => rx.test(urlOrPath)
  }

  // Pattern has a query part — match pathname exactly, then check each
  // key=value pair in the pattern against URLSearchParams (order-independent)
  const patternPath = pattern.slice(0, qIndex)
  const patternSearch = pattern.slice(qIndex + 1)

  const pathRx = new RegExp('^' + escapeForRegex(patternPath) + '$')

  const requiredParams = patternSearch.split('&').map((pair) => {
    const eqIdx = pair.indexOf('=')

    if (eqIdx === -1) return { key: pair, valueRx: /.*/ }
    const key = pair.slice(0, eqIdx)
    const value = pair.slice(eqIdx + 1)
    const valueRx = new RegExp('^' + escapeForRegex(value) + '$')

    return { key, valueRx }
  })

  return (urlOrPath) => {
    const hashStripped = urlOrPath.split('#')[0]
    const qIdx = hashStripped.indexOf('?')
    const pathname = qIdx === -1 ? hashStripped : hashStripped.slice(0, qIdx)
    const search = qIdx === -1 ? '' : hashStripped.slice(qIdx + 1)

    if (!pathRx.test(pathname)) return false

    const params = new URLSearchParams(search)

    return requiredParams.every(({ key, valueRx }) => {
      const val = params.get(key)

      return val !== null && valueRx.test(val)
    })
  }
}

const createMatcher = (
  patterns: string | string[],
  opts?: MatchOptions
): ((urlOrPath: string) => boolean) => {
  const list = Array.isArray(patterns) ? patterns : [patterns]

  if (list.length === 0) return () => false

  const matchers = list.map((p) => createSingleMatcher(p, opts))

  return (urlOrPath: string) => matchers.some((fn) => fn(urlOrPath))
}

export const matches = (
  patterns: string | string[],
  urlOrPath: string,
  opts?: MatchOptions
): boolean => {
  return createMatcher(patterns, opts)(urlOrPath)
}
